import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";
import { identifyPostHog, resetPostHog, capturePostHog, PHEvents } from "@/lib/posthog";


export type UserRole = "freelancer" | "client";
export type SubscriptionTier = "free" | "starter" | "pro" | "unlimited";
export type KycStatus = "not_started" | "pending" | "verified" | "rejected";
export type PayoutMethod = "binance_pay" | "usdt_bep20";

export interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  role: UserRole;
  usdt_address: string | null;
  binance_pay_id: string | null;
  payout_method: PayoutMethod | null;
  avatar_url: string | null;
  subscription_tier: SubscriptionTier;
  projects_created_this_month: number;
  projects_bonus_slots: number;
  projects_month_anchor: string;
  kyc_status: KycStatus;
  kyc_duplicate: boolean;
  kyc_last_reason: string | null;
  stripe_verification_session_id: string | null;
  wallet_unlock_until: string | null;
  payout_unlock_until: string | null;
  notification_sound_enabled: boolean;
  portfolio_unlocked: boolean;
  portfolio_username: string | null;
  phone: string | null;
  phone_verified: boolean;
  email_verified: boolean;
  subscription_status: string | null;
  subscription_current_period_end: string | null;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const lastKycRef = useRef<string | null>(null);

  async function loadProfile(uid: string) {
    const { data } = await supabase
      .from("profiles")
      .select("id, email, full_name, role, usdt_address, binance_pay_id, payout_method, avatar_url, subscription_tier, projects_created_this_month, projects_bonus_slots, projects_month_anchor, kyc_status, kyc_duplicate, kyc_last_reason, stripe_verification_session_id, wallet_unlock_until, payout_unlock_until, notification_sound_enabled, portfolio_unlocked, portfolio_username, phone, phone_verified, email_verified, subscription_status, subscription_current_period_end")
      .eq("id", uid)
      .maybeSingle();
    const p = (data as Profile | null) ?? null;
    setProfile(p);
    if (p) {
      try {
        Sentry.setUser({ id: p.id });
        Sentry.setTag("role", p.role);
        Sentry.setTag("subscription_plan", p.subscription_tier);
      } catch { /* noop */ }
      identifyPostHog(p.id, {
        email: p.email,
        role: p.role,
        subscription_plan: p.subscription_tier,
      });
      const prev = lastKycRef.current;
      if (prev !== null && prev !== "verified" && p.kyc_status === "verified") {
        capturePostHog(PHEvents.KycVerified);
      }
      lastKycRef.current = p.kyc_status;
    }
  }


  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        if (event === "SIGNED_IN") {
          capturePostHog(PHEvents.Login, { user_id: s.user.id });
        }
        setTimeout(() => loadProfile(s.user.id), 0);
      } else {
        setProfile(null);
        try { Sentry.setUser(null); } catch { /* noop */ }
        if (event === "SIGNED_OUT") {
          capturePostHog(PHEvents.Logout);
          resetPostHog();
        }
      }

    });

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) loadProfile(s.user.id).finally(() => setLoading(false));
      else setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const value: AuthContextValue = {
    user,
    session,
    profile,
    loading,
    refreshProfile: async () => {
      if (user) await loadProfile(user.id);
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
